// 필드 구역 1(지역1 이끼 유적) 지도 생성 — 순수·결정적(같은 씨앗이면 같은 지도). three 없음.
// 32m 청크 5×5. 청크마다 지역1 스테이지 지도의 모양 말(shapes.ts: ellipse·disk·path)로 공터 하나 + 이웃 청크로 가는 길을 만든다.
// 이웃 청크 사이의 "문"(포트)은 두 청크가 같은 자리·같은 수직 구간을 그리므로 경계에서 길이 그대로 이어진다.
// 판정은 청크마다 32×32 1m 격자(FieldChunkDef.grid)이고, 게임은 그 격자들을 겹쳐 놓은 구역 격자(FieldDef.grid)를 쓴다.
// 가장자리 모양·랜드마크 종류는 지역1 스테이지 지도 네 가지 판(a 절벽 유적·b 수로·m 고블린 캠프·f 고목 뿌리)의 말을 그대로 쓴다(렌더가 같은 세트피스를 깐다).
import { Cell, type EdgeStyle, type Landmark, type MapDef, type MapGrid, type Rect } from '../levels';
import { cellsOf, disk, ellipse, path, rectsOfCells } from '../shapes';
import { CHUNK_COUNT, FIELD, FIELD_GRID_SPEC as G } from './constants';

export type ChunkTheme = 'a' | 'b' | 'm' | 'f';
/** 무리 자리 하나 */
export interface PackSpot {
  readonly x: number;
  readonly z: number;
  /** 마리 수(3~6) */
  readonly size: number;
  /** 종류 순서(MobKind 번호, size개) */
  readonly kinds: readonly number[];
  /** 레벨 흔들림(-spread..+spread, 무리마다 고정) */
  readonly levelOffset: number;
}
export interface FieldPoint { readonly x: number; readonly z: number }
export interface FieldChunkDef {
  readonly index: number;
  readonly cx: number;
  readonly cz: number;
  /** 청크 북서 모서리(월드) */
  readonly x0: number;
  readonly z0: number;
  readonly theme: ChunkTheme;
  readonly edgeStyle: EdgeStyle;
  /** 걸을 수 있는 칸(월드 좌표 사각형, 이 청크 안만) */
  readonly floors: readonly Rect[];
  readonly landmarks: readonly Landmark[];
  /** 렌더 전용 수면(빈칸 쪽) */
  readonly water: readonly Rect[];
  /** 공터 가운데(이벤트 지점) */
  readonly center: FieldPoint;
  readonly packs: readonly PackSpot[];
  /** 이벤트 지점(입구 청크는 없음) */
  readonly event: FieldPoint | null;
  /** 이 청크 판정 격자(32×32, 원점 = x0, z0) */
  readonly grid: MapGrid;
  /** 열린 포트 방향 비트: 1 북 2 동 4 남 8 서 */
  readonly ports: number;
}
/** 발을 올리면 반응하는 자리(원) */
export interface FieldTrigger { readonly x: number; readonly z: number; readonly r: number }
export interface FieldDef {
  readonly id: 'zone1';
  readonly name: string;
  readonly chunks: readonly FieldChunkDef[];
  /** 구역 전체 지도(게임 판정·렌더 공용 데이터). id는 지역 r1(몹 공세·드롭 표가 지역1을 따른다) */
  readonly map: MapDef;
  /** 구역 격자 = 청크 격자 25장을 제자리에 겹친 것 */
  readonly grid: MapGrid;
  readonly spawn: { readonly x: number; readonly z: number; readonly facing: number };
  /** 남쪽 출구(마을로) */
  readonly exit: Rect;
  readonly returnStone: FieldTrigger;
  readonly riftPortal: FieldTrigger;
  readonly hidden: FieldTrigger & { readonly chunk: number };
}

const rc = (x0: number, z0: number, x1: number, z1: number): Rect => ({ x0, z0, x1, z1 });
const N = Math.PI;

function lcg(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (Math.imul(a, 1664525) + 1013904223) >>> 0; return a / 4294967296; };
}
/** 정수 둘 → 씨앗(청크·경계마다 따로 굴린다) */
function mix(seed: number, a: number, b: number): number {
  let h = (seed ^ Math.imul(a + 0x9e37, 0x85ebca6b) ^ Math.imul(b + 0x7f4a, 0xc2b2ae35)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 13)) >>> 0;
}

export const chunkIndex = (cx: number, cz: number): number => cz * G.cols + cx;
/** 월드 좌표 → 청크 번호(구역 밖이면 -1) */
export function chunkAt(x: number, z: number): number {
  const cx = Math.floor((x - G.x0) / G.chunk), cz = Math.floor((z - G.z0) / G.chunk);
  if (cx < 0 || cz < 0 || cx >= G.cols || cz >= G.rows) return -1;
  return chunkIndex(cx, cz);
}

/** 입구 청크(남쪽 가운데)·귀환석 청크(가운데)·균열 청크(북쪽 가운데) */
export const ENTRY_CHUNK = chunkIndex(2, 4);
export const STONE_CHUNK = chunkIndex(2, 2);
export const RIFT_CHUNK = chunkIndex(2, 1);

/** 청크 사이 경계(가로 = 동쪽 이웃, 세로 = 남쪽 이웃)를 연다: 무작위 신장 트리 + 덧길. 결과는 경계 키 집합 */
function openEdges(seed: number): Set<string> {
  const rnd = lcg(mix(seed, 1, 1));
  const seen = new Uint8Array(CHUNK_COUNT);
  const open = new Set<string>();
  const key = (a: number, b: number): string => Math.min(a, b) + '-' + Math.max(a, b);
  const nbr = (i: number): number[] => {
    const cx = i % G.cols, cz = Math.floor(i / G.cols), out: number[] = [];
    if (cz > 0) out.push(i - G.cols);
    if (cx < G.cols - 1) out.push(i + 1);
    if (cz < G.rows - 1) out.push(i + G.cols);
    if (cx > 0) out.push(i - 1);
    return out;
  };
  const stack = [ENTRY_CHUNK];
  seen[ENTRY_CHUNK] = 1;
  while (stack.length) {
    const cur = stack[stack.length - 1];
    const cand = nbr(cur).filter((n) => !seen[n]);
    if (cand.length === 0) { stack.pop(); continue; }
    const n = cand[Math.floor(rnd() * cand.length)];
    seen[n] = 1;
    open.add(key(cur, n));
    stack.push(n);
  }
  for (let i = 0; i < CHUNK_COUNT; i++) for (const n of nbr(i)) if (n > i && !open.has(key(i, n)) && rnd() < 0.4) open.add(key(i, n));
  // 입구·귀환석·균열 청크가 세로로 곧게 이어지게(첫 동선이 북쪽으로 읽힌다)
  open.add(key(ENTRY_CHUNK, chunkIndex(2, 3))); open.add(key(chunkIndex(2, 3), STONE_CHUNK)); open.add(key(STONE_CHUNK, RIFT_CHUNK));
  return open;
}

/** 경계 위 포트 자리(경계 시작에서 m): 두 청크가 같은 값을 쓴다 */
function portOffset(seed: number, a: number, b: number): number {
  const r = lcg(mix(seed, Math.min(a, b) + 100, Math.max(a, b) + 200));
  return 10 + Math.floor(r() * 13); // 10..22
}
const PATH_W = 6;
const THEMES: readonly ChunkTheme[] = ['a', 'f', 'm', 'b'];
const EDGE_OF: Readonly<Record<ChunkTheme, EdgeStyle>> = { a: 'cliff', b: 'water', m: 'palisade', f: 'roots' };

/** 칸 집합에서 (x, z) 둘레 r m가 모두 바닥인지 */
function roomy(cells: ReadonlySet<string>, x: number, z: number, r: number): boolean {
  for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) if (!cells.has(Math.floor(x + dx) + ',' + Math.floor(z + dz))) return false;
  return true;
}

/** 칸 집합 연결 성분 크기(시작 칸에서 4방향 넓이 우선) */
export function reach(cells: ReadonlySet<string>, sx: number, sz: number): Set<string> {
  const start = Math.floor(sx) + ',' + Math.floor(sz), out = new Set<string>();
  if (!cells.has(start)) return out;
  const q = [start];
  out.add(start);
  while (q.length) {
    const [x, z] = q.pop()!.split(',').map(Number);
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const k = (x + dx) + ',' + (z + dz);
      if (cells.has(k) && !out.has(k)) { out.add(k); q.push(k); }
    }
  }
  return out;
}

function gridOf(x0: number, z0: number, w: number, h: number, floor: ReadonlySet<string>, solid: ReadonlySet<string>): MapGrid {
  const cells = new Uint8Array(w * h);
  for (let z = 0; z < h; z++) for (let x = 0; x < w; x++) {
    const k = (x0 + x) + ',' + (z0 + z);
    cells[z * w + x] = solid.has(k) ? Cell.Solid : floor.has(k) ? Cell.Floor : Cell.Void;
  }
  return { originX: x0, originZ: z0, width: w, height: h, cell: 1, cells, gateCells: new Uint8Array(w * h), lavaCells: new Uint8Array(w * h) };
}

interface Special { exit?: boolean; stone?: boolean; rift?: boolean; hidden?: 'nw' | 'ne' | 'sw' | 'se' }

function buildChunk(seed: number, index: number, open: ReadonlySet<string>, special: Special): FieldChunkDef & { hiddenAt?: FieldPoint; extra: { stone?: FieldPoint; rift?: FieldPoint; exit?: Rect; spawn?: FieldPoint } } {
  const cx = index % G.cols, cz = Math.floor(index / G.cols);
  const x0 = G.x0 + cx * G.chunk, z0 = G.z0 + cz * G.chunk, x1 = x0 + G.chunk, z1 = z0 + G.chunk;
  const rnd = lcg(mix(seed, cx + 10, cz + 20));
  const theme: ChunkTheme = index === ENTRY_CHUNK || index === STONE_CHUNK ? 'a' : index === RIFT_CHUNK ? 'f' : THEMES[(cx * 3 + cz * 2 + Math.floor(rnd() * 2)) % THEMES.length];
  const ccx = x0 + 16 + Math.round((rnd() - 0.5) * 6), ccz = z0 + 16 + Math.round((rnd() - 0.5) * 6);
  const rx = 8 + Math.floor(rnd() * 4), rz = 7 + Math.floor(rnd() * 4);
  const shapes: Rect[][] = [];
  shapes.push(theme === 'm' ? disk(ccx, ccz, Math.min(rx, rz) + 1) : ellipse(ccx, ccz, rx, rz));
  // 수로(b): 공터 곁 작은 광장 하나 더(지역1 B 수로 광장의 원형 광장 두 개처럼)
  if (theme === 'b') shapes.push(disk(ccx + (rnd() < 0.5 ? -6 : 6), ccz + (rnd() < 0.5 ? -5 : 5), 5));
  // 포트: 북 동 남 서
  let ports = 0;
  const pathCells = new Set<string>();
  const addPath = (pts: [number, number][]): void => { const p = path(pts, PATH_W); shapes.push(p); for (const k of cellsOf(p)) pathCells.add(k); };
  const dirs: [number, number, number][] = [[0, -1, 1], [1, 0, 2], [0, 1, 4], [-1, 0, 8]];
  for (const [dx, dz, bit] of dirs) {
    const nx = cx + dx, nz = cz + dz;
    if (nx < 0 || nz < 0 || nx >= G.cols || nz >= G.rows) continue;
    const ni = chunkIndex(nx, nz);
    if (!open.has(Math.min(index, ni) + '-' + Math.max(index, ni))) continue;
    ports |= bit;
    const off = portOffset(seed, index, ni) + 0.5;
    // 경계 위 포트 점 · 경계 안쪽 5m · 경계 바깥 2m(잘려 나간다). 마지막 구간은 경계에 수직이라 두 청크의 길이 겹친다
    let ex: number, ez: number, ix: number, iz: number, ox: number, oz: number;
    if (dz !== 0) { ex = x0 + off; ez = dz < 0 ? z0 : z1; ix = ex; iz = ez - dz * 5; ox = ex; oz = ez + dz * 2; }
    else { ez = z0 + off; ex = dx < 0 ? x0 : x1; iz = ez; ix = ex - dx * 5; ox = ex + dx * 2; oz = ez; }
    const mx = (ccx + ix) / 2 + (rnd() - 0.5) * 4, mz = (ccz + iz) / 2 + (rnd() - 0.5) * 4;
    addPath([[ccx, ccz], [mx, mz], [ix, iz], [ox, oz]]);
  }
  const extra: { stone?: FieldPoint; rift?: FieldPoint; exit?: Rect; spawn?: FieldPoint } = {};
  if (special.exit) {
    // 남쪽 출구: 공터에서 구역 남쪽 끝까지 곧은 길. 출구 칸은 끝 3m
    const ex = Math.round(ccx) + 0.5;
    addPath([[ccx, ccz], [ex, ccz + 6], [ex, z1 + 2]]);
    extra.exit = rc(Math.floor(ex) - 2, z1 - 3, Math.floor(ex) + 3, z1);
    extra.spawn = { x: ex, z: z1 - 9 };
  }
  let hiddenAt: FieldPoint | undefined;
  if (special.hidden) {
    // 숨은 던전 입구: 공터에서 청크 구석으로 꺾여 들어가는 막다른 길 끝의 작은 둥근 자리
    const sx = special.hidden.endsWith('w') ? x0 + 6.5 : x1 - 6.5, sz = special.hidden.startsWith('n') ? z0 + 6.5 : z1 - 6.5;
    addPath([[ccx, ccz], [(ccx + sx) / 2, ccz], [sx, sz]]);
    shapes.push(disk(sx, sz, 3.2));
    hiddenAt = { x: sx, z: sz };
  }
  // 이 청크 안 칸만(바깥 2m로 내민 길 끝은 잘린다)
  const floor = new Set<string>();
  for (const list of shapes) for (const k of cellsOf(list)) {
    const [x, z] = k.split(',').map(Number);
    if (x >= x0 && x < x1 && z >= z0 && z < z1) floor.add(k);
  }
  // 랜드마크: 공터 가장자리 둘레, 길 칸·특수 자리를 피하고, 놓은 뒤에도 공터 가운데에서 모든 바닥에 닿아야 한다
  const landmarks: Landmark[] = [];
  const solid = new Set<string>();
  const keepClear: FieldPoint[] = [{ x: ccx, z: ccz }];
  if (special.stone) { extra.stone = { x: ccx + 0.5, z: ccz + 4.5 }; keepClear.push(extra.stone); }
  if (special.rift) { extra.rift = { x: ccx + 0.5, z: ccz - 2.5 }; keepClear.push(extra.rift); }
  if (extra.spawn) keepClear.push(extra.spawn);
  if (hiddenAt) keepClear.push(hiddenAt);
  const lmKind: Landmark['kind'][] = theme === 'a' ? ['pillar', 'pillar', 'pillar', 'rubble'] : theme === 'm' ? ['tower', 'bonfire', 'tower'] : theme === 'f' ? ['root', 'root', 'root'] : ['pillar', 'rubble'];
  const total = floor.size;
  for (const kind of lmKind) {
    for (let t = 0; t < 12; t++) {
      const a = rnd() * Math.PI * 2, rr = 0.55 + rnd() * 0.3;
      const lx = Math.floor(ccx + Math.cos(a) * rx * rr), lz = Math.floor(ccz + Math.sin(a) * rz * rr);
      const w = 2, d = 2;
      const rect = rc(lx, lz, lx + w, lz + d);
      const cells = cellsOf([rect]);
      let ok = true;
      for (const k of cells) if (!floor.has(k) || pathCells.has(k) || solid.has(k)) { ok = false; break; }
      if (!ok) continue;
      // 둘레 1칸도 바닥이어야(가장자리에 붙어 길목을 막지 않게)
      if (!roomy(floor, lx + 1, lz + 1, 2)) continue;
      if (keepClear.some((p) => Math.hypot(p.x - (lx + 1), p.z - (lz + 1)) < 4.5)) continue;
      const trial = new Set(floor);
      for (const k of solid) trial.delete(k);
      for (const k of cells) trial.delete(k);
      if (reach(trial, ccx, ccz).size !== total - solid.size - cells.size) continue;
      for (const k of cells) solid.add(k);
      const height = kind === 'pillar' ? 4.5 : kind === 'rubble' ? 1.2 : kind === 'tower' ? 6.5 : kind === 'bonfire' ? 1.2 : 3;
      landmarks.push({ kind, rect, height, solid: true });
      break;
    }
  }
  // 귀환석: 1칸 돌(충돌), 곁에 서면 반응한다
  if (extra.stone) {
    const k = Math.floor(extra.stone.x) + ',' + Math.floor(extra.stone.z);
    solid.add(k);
    landmarks.push({ kind: 'statue', label: '귀환석', rect: rc(Math.floor(extra.stone.x), Math.floor(extra.stone.z), Math.floor(extra.stone.x) + 1, Math.floor(extra.stone.z) + 1), height: 2.2, solid: true });
  }
  // 수면(b): 청크 안 빈칸 전체를 물로 그린다
  const water = theme === 'b' ? [rc(x0, z0, x1, z1)] : [];
  // 무리 자리: 공터 안, 가운데에서 3~7m, 서로 6m 이상, 둘레 2m가 비어 있는 곳
  const packs: PackSpot[] = [];
  const want = index === ENTRY_CHUNK ? 1 : rnd() < 0.5 ? 2 : 1;
  const walk = new Set(floor);
  for (const k of solid) walk.delete(k);
  for (let t = 0; t < 40 && packs.length < want; t++) {
    const a = rnd() * Math.PI * 2, dd = 3 + rnd() * 4;
    const px = Math.floor(ccx + Math.cos(a) * dd) + 0.5, pz = Math.floor(ccz + Math.sin(a) * dd) + 0.5;
    if (!roomy(walk, px, pz, 2)) continue;
    if (packs.some((p) => Math.hypot(p.x - px, p.z - pz) < 6)) continue;
    if (extra.spawn && Math.hypot(extra.spawn.x - px, extra.spawn.z - pz) < 14) continue;
    if (extra.stone && Math.hypot(extra.stone.x - px, extra.stone.z - pz) < 4) continue;
    const size = FIELD.packMin + Math.floor(rnd() * (FIELD.packMax - FIELD.packMin + 1));
    const kinds: number[] = [];
    for (let i = 0; i < size; i++) kinds.push(i === 0 && size >= 5 && rnd() < 0.5 ? 2 : i === 1 && size >= 4 ? 1 : 0);
    packs.push({ x: px, z: pz, size, kinds, levelOffset: Math.floor(rnd() * (FIELD.levelSpread * 2 + 1)) - FIELD.levelSpread });
  }
  const center = { x: Math.floor(ccx) + 0.5, z: Math.floor(ccz) + 0.5 };
  return {
    index, cx, cz, x0, z0, theme, edgeStyle: EDGE_OF[theme], floors: rectsOfCells(floor), landmarks, water, center, packs,
    event: index === ENTRY_CHUNK ? null : center, grid: gridOf(x0, z0, G.chunk, G.chunk, floor, solid), ports, hiddenAt, extra,
  };
}

/** 구역 정의를 만든다(같은 씨앗 → 같은 결과). 게임은 FIELD_ZONE1 하나만 쓴다 */
export function buildFieldDef(seed: number = FIELD.seed): FieldDef {
  const open = openEdges(seed);
  const rnd = lcg(mix(seed, 7, 7));
  // 숨은 던전: 네 구석 청크 중 하나(입구에서 먼 북쪽 둘 중)
  const hiddenChunk = rnd() < 0.5 ? chunkIndex(0, 0) : chunkIndex(G.cols - 1, 0);
  const chunks = [] as ReturnType<typeof buildChunk>[];
  for (let i = 0; i < CHUNK_COUNT; i++) {
    chunks.push(buildChunk(seed, i, open, {
      exit: i === ENTRY_CHUNK, stone: i === STONE_CHUNK, rift: i === RIFT_CHUNK,
      hidden: i === hiddenChunk ? (i === chunkIndex(0, 0) ? 'nw' : 'ne') : undefined,
    }));
  }
  // 구역 격자: 청크 격자를 제자리에 복사
  const W = G.cols * G.chunk, H = G.rows * G.chunk;
  const cells = new Uint8Array(W * H);
  for (const c of chunks) for (let z = 0; z < G.chunk; z++) for (let x = 0; x < G.chunk; x++) {
    cells[(c.z0 - G.z0 + z) * W + (c.x0 - G.x0 + x)] = c.grid.cells[z * G.chunk + x];
  }
  const grid: MapGrid = { originX: G.x0, originZ: G.z0, width: W, height: H, cell: 1, cells, gateCells: new Uint8Array(W * H), lavaCells: new Uint8Array(W * H) };
  const entry = chunks[ENTRY_CHUNK].extra, stone = chunks[STONE_CHUNK].extra.stone!, rift = chunks[RIFT_CHUNK].extra.rift!;
  const hc = chunks[hiddenChunk];
  const map: MapDef = {
    id: 'r1',
    name: '이끼 유적 필드',
    floors: chunks.flatMap((c) => c.floors),
    landmarks: chunks.flatMap((c) => c.landmarks),
    gates: [], rooms: [], exits: [],
    spawn: { x: entry.spawn!.x, z: entry.spawn!.z, facing: N },
    wallHeight: 3.2,
    edgeStyle: 'cliff',
    water: chunks.flatMap((c) => c.water),
  };
  const def: FieldDef = {
    id: 'zone1', name: '이끼 유적 필드', chunks: chunks.map(({ hiddenAt: _h, extra: _e, ...c }) => c), map, grid,
    spawn: map.spawn, exit: entry.exit!,
    returnStone: { x: stone.x, z: stone.z, r: 2.2 },
    riftPortal: { x: rift.x, z: rift.z, r: 1.8 },
    hidden: { x: hc.hiddenAt!.x, z: hc.hiddenAt!.z, r: 1.6, chunk: hiddenChunk },
  };
  return def;
}

let zone1: FieldDef | null = null;
/** 구역 1(처음 부를 때 한 번 만든다) */
export function fieldZone1(): FieldDef { return (zone1 ??= buildFieldDef()); }

/**
 * 마을① 필드 문(마을 좌표): 북쪽 벽 서쪽 끝, 이끼 유적 문(x −3..3) 옆 광장 바닥. 마을 지도 데이터(villageMaps·levels)는 바꾸지 않는다 —
 * 문 판정은 필드 컨트롤러가, 문 그림은 render/field가 따로 한다. 잡화상 NPC(−9.1, −8.5)·상점 건물(z ≥ −12)과 겹치지 않는다
 */
export const FIELD_DOOR: Rect = rc(-15, -16, -11, -13);

/** 월드 맵 구역 목록(지금은 1구역만 열림, 나머지 5구역은 잠김 표시) */
export const FIELD_ZONES: readonly { readonly id: string; readonly name: string; readonly region: number; readonly open: boolean }[] = [
  { id: 'zone1', name: '이끼 유적 필드', region: 1, open: true },
  { id: 'zone2', name: '잿불 협곡 필드', region: 2, open: false },
  { id: 'zone3', name: '서리 묘지 필드', region: 3, open: false },
  { id: 'zone4', name: '가라앉은 사원 필드', region: 4, open: false },
  { id: 'zone5', name: '황금 모래 폐도 필드', region: 5, open: false },
  { id: 'zone6', name: '심연의 성채 필드', region: 6, open: false },
];
